import { useQuery } from "@tanstack/react-query";
import type { SourceAttribution } from "@/lib/source-attribution";
import { apiCall } from "./core";
import type { BrowserHandoff } from "./notifications";

export type ActivityCard = SourceAttribution & {
	id: string;
	kind: string;
	title: string;
	body: string | null;
	at: string;
	status: string | null;
	count: number;
	href: string | null;
	unread?: boolean;
	notification_id?: number;
	browser_url?: string | null;
	browser_handoff?: BrowserHandoff | null;
	run_id?: number;
	/**
	 * Kind of pending interaction behind this notification (events
	 * `interaction_type` vocabulary — 'approval' today). Clients pick the UI
	 * for the type; new interaction kinds extend the value, not the card shape.
	 */
	interaction_type?: string;
	/**
	 * Live interaction state from its authoritative per-type source. For
	 * 'approval': 'pending' | 'approved' | 'rejected' (the run's state machine).
	 */
	interaction_status?: string;
	/** Server policy: this interaction can be completed inline from the feed. */
	interaction_inline?: boolean;
	/**
	 * A decision with named outcomes rather than approve/reject: the options to
	 * render as buttons and the schema field their value answers. Answer with
	 * `{ [interaction_choice_field]: value }` — the field is required, so a bare
	 * approve would record an empty answer.
	 */
	interaction_choice_field?: string;
	interaction_choices?: Array<{ value: string; label: string }>;
	member_run_ids?: number[];
	/** Source attribution for notification-backed activity cards. */
};

type ListActivityResult = {
	action: "list_activity";
	items: ActivityCard[];
	total: number;
	limit: number;
};

type ActivityFeedOpts = {
	limit?: number;
	includeNotifications?: boolean;
	includeRuns?: boolean;
	aggregate?: boolean;
	kinds?: string[];
	/** Scope the feed to a single agent's runs (drops notifications server-side). */
	agentId?: string;
};

/**
 * Workspace attention feed (manage_operations.list_activity) — aggregated
 * notifications + user-facing runs with deep-links.
 */
export function useActivityFeed(
	ownerSlug: string | null,
	opts?: ActivityFeedOpts,
) {
	const limit = opts?.limit ?? 24;
	const aggregate = opts?.aggregate !== false;
	const includeNotifications = opts?.includeNotifications !== false;
	const includeRuns = opts?.includeRuns !== false;
	const kinds = opts?.kinds;
	const agentId = opts?.agentId;

	return useQuery({
		queryKey: [
			"activity-feed",
			ownerSlug ?? null,
			limit,
			aggregate,
			includeNotifications,
			includeRuns,
			kinds?.join(",") ?? null,
			agentId ?? null,
		],
		queryFn: async () => {
			const res = await apiCall<ListActivityResult>(
				"manage_operations",
				{
					action: "list_activity",
					limit,
					aggregate,
					include_notifications: includeNotifications,
					include_runs: includeRuns,
					...(kinds?.length ? { kinds } : {}),
					...(agentId ? { agent_id: agentId } : {}),
				},
				ownerSlug ? { slug: ownerSlug } : undefined,
			);
			return res.items ?? [];
		},
		enabled: !!ownerSlug,
		staleTime: 15_000,
		refetchInterval: (query) =>
			query.state.data?.some(
				(card) =>
					card.browser_handoff?.state === "ready" ||
					card.browser_handoff?.state === "preparing",
			)
				? 3_000
				: 30_000,
	});
}
